import React from "react";
import {
  OrderPriceTaxBreakdown,
  PricingBreakDown,
} from "@api/graphql/generated/generated-types";
import { Badge } from "@src/common/components";
import {
  EiaFuelRateNote,
  EiaFuelRateDisplay,
} from "@src/common/components/EiaFuelRateNote";
import { useCourierLocale } from "@src/common/courierLocale";

/**
 * Only what this component renders. Structural rather than the full
 * PricingBreakDown so callers can pass fragment results, which select a partial
 * surchargePricingRule and therefore do not satisfy the whole schema type.
 */
interface PricingBreakdownContentItem {
  name: string;
  amount: any;
  description?: string | null;
  surchargePricingRuleId?: string | null;
}

interface PricingBreakdownContentProps {
  pricingBreakDown: { items: PricingBreakdownContentItem[] };
  total: number | string;
  pricingListName?: string;
  taxBreakDown?: OrderPriceTaxBreakdown | null;
  /** From useEiaFuelRateByRuleId; absent on screens that don't show EIA context. */
  eiaFuelRateByRuleId?: Record<string, EiaFuelRateDisplay>;
  totalAfterTax?: number | string;
  compact?: boolean;
}

export function PricingBreakdownContent({
  pricingBreakDown,
  eiaFuelRateByRuleId,
  total,
  pricingListName,
  taxBreakDown,
  totalAfterTax,
  compact = false,
}: PricingBreakdownContentProps) {
  const { currencyDisplay } = useCourierLocale();
  const paddingClass = compact ? "py-1" : "py-2";
  const totalPaddingClass = compact ? "py-2" : "py-4";
  const textSizeClass = compact ? "text-xs" : "text-sm";

  return (
    <div className={compact ? "" : "container mx-auto"}>
      <table className="w-full border-collapse">
        <tbody>
          {pricingListName && (
            <tr>
              <td className={totalPaddingClass}>
                <div className={`font-semibold ${textSizeClass}`}>Pricing List</div>
              </td>
              <td className={`font-semibold ${totalPaddingClass} flex`}>
                <Badge color="purple">{pricingListName}</Badge>
              </td>
            </tr>
          )}
          {pricingBreakDown.items.map((item, index) => (
            <tr key={index}>
              <td className={paddingClass}>
                <div className="flex flex-col">
                  <p className={textSizeClass}>{item.name}</p>
                </div>
                {item.description && (
                  <div className="text-slate-400">
                    <p className={compact ? "text-xs" : ""}>{item.description}</p>
                  </div>
                )}
                <EiaFuelRateNote
                  eiaFuelRate={
                    item.surchargePricingRuleId
                      ? eiaFuelRateByRuleId?.[item.surchargePricingRuleId]
                      : undefined
                  }
                  compact={compact}
                />
              </td>
              <td className={`${paddingClass} ${textSizeClass}`}>{currencyDisplay}{item.amount}</td>
            </tr>
          ))}
          <tr>
            <td className={totalPaddingClass}>
              <div className={`font-semibold ${textSizeClass}`}>Total</div>
            </td>
            <td className={`font-semibold ${totalPaddingClass} ${textSizeClass}`}>{currencyDisplay}{total}</td>
          </tr>
          {taxBreakDown?.taxItems && taxBreakDown.taxItems.length > 0 && (
            <>
              <tr className={`${compact ? "text-xs" : "text-sm"} text-slate-400`}>
                <td className="py-1">Total Before Tax</td>
                <td className="py-1">{currencyDisplay}{total}</td>
              </tr>
              {taxBreakDown.taxItems.map((item, index) => (
                <tr key={`tax-${index}`} className={`${compact ? "text-xs" : "text-sm"} text-slate-400`}>
                  <td className="py-1">{item.name}</td>
                  <td className="py-1">{currencyDisplay}{item.amount}</td>
                </tr>
              ))}
              <tr className={`${compact ? "text-xs" : "text-sm"} text-slate-500`}>
                <td className={paddingClass}>Total After Tax</td>
                <td className={paddingClass}>{currencyDisplay}{totalAfterTax}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
